import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Button,
  ButtonProps,
  CardHeader,
  CardTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Icons,
  InfiniteScroll,
  Input,
  Label,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Progress,
  Separator,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@ais-ui/ui-core';
import { XIcon } from 'lucide-react';
import {
  PiCameraPlus,
  PiDotsThreeVertical,
  PiListBullets,
} from 'react-icons/pi';
import { CreateReportTemplate } from '../Reports/Mutations/CreateReportTemplate/CreateReportTemplate';
import { CloneReport } from '../Reports/Mutations/CloneReport/CloneReport';
import {
  Job,
  ReportBackendTypes,
  ReportTemplate,
  useGetReport,
  useGetReportTemplates,
} from '@ais-ui/websockets';
import { reportTypesObject } from '../Reports/reportsTypes';
import React, { useEffect, useState } from 'react';
import { cn, isNonEmpty, useTimeZoneContext } from '@ais-ui/utils';
import { useDateTimeFormat } from '../conversions/useDateFormat';
import { useExportReport } from '../Reports/useExportReport';
import { ReportDelete } from '../Reports/Mutations/DeleteReport';
import { AbortReport } from '../Reports/Mutations/AbortReport';
import { noop } from 'lodash';
import {
  CreateReportProvider,
  useCreateReportContext,
} from '../Reports/Mutations/CreateReportTemplate/CreateReportContext';
import { useReplayRange } from '../../../../../libs/websockets/src/lib/replay-server/useReplayRange';
import { useReplayContext } from './ReplayContext';
import {
  FlattenedReplayVideo,
  useReplayVideoFilters,
} from './useReplayVideoFilters';
import { ReplayVideoFilter } from './ReplayVideoFilter';
import { ReplayVideoSort } from './ReplayVideoSort';

interface ReplayVideoItemProps {
  reportTemplate: ReportTemplate;
  report: Job;
}

interface ReplayDropDownMenuProps {
  report: Job;
  reportTemplate: ReportTemplate;
  onDelete: () => void;
  onClone: () => void;
  onAbort: () => void;
  className?: string;
  id?: string;
}

const ReplayDropDownMenu = ({
  report,
  reportTemplate,
  onDelete,
  onClone,
  onAbort,
  className,
  id,
}: ReplayDropDownMenuProps) => {
  const { onExport, activeExportFormat } = useExportReport(report.uuid);
  const downloadDisabled = report.status !== 'FINISHED' || !!activeExportFormat;
  const canAbort = report.status === 'EXECUTING' || report.status === 'PENDING';

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className={className}>
        <Button id={id} size="icon" variant="ghost" className={cn('size-8')}>
          <PiDotsThreeVertical className="size-6" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          disabled={downloadDisabled}
          onClick={() => onExport('OF_VIDEO')}
        >
          {activeExportFormat ? (
            <Icons.spinner className="size-4 animate-spin" />
          ) : null}
          Download
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onClone}>
          Clone
        </DropdownMenuItem>
        {canAbort && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onAbort} className="text-destructive">
              Abort
            </DropdownMenuItem>
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onDelete}>Delete</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface ReplayVideoGridItemProps {
  label: string;
  value: string | undefined | null;
}
const ReplayVideoGridItem = ({ label, value }: ReplayVideoGridItemProps) => {
  return (
    <div className="flex flex-col gap-1">
      <p className="text-sm font-medium flex-none">{label}</p>
      <p className="text-sm text-muted-foreground flex-grow text-ellipsis overflow-hidden">
        {isNonEmpty(value) ? value : 'Unknown'}
      </p>
    </div>
  );
};

interface ReplayVideoDetailsProps {
  report: Job;
}

const ReplayVideoDetails = ({ report }: ReplayVideoDetailsProps) => {
  const { timeZone } = useTimeZoneContext();
  const { data: reportDetails, isLoading } = useGetReport(
    report.uuid,
    timeZone,
  );
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();

  return (
    <>
      {isLoading && <LoadingSpinner size="small"></LoadingSpinner>}
      {!isLoading && reportDetails && (
        <div className="grid gap-6 px-4 pt-4">
          <div className="grid gap-4">
            <div className="grid grid-cols-2 gap-4">
              <ReplayVideoGridItem label="ID" value={report.uuid} />
              <ReplayVideoGridItem
                label="Shape"
                value={reportDetails?.info?.Area}
              />
              <ReplayVideoGridItem
                label="Status"
                value={reportDetails?.info?.Status}
              />
              <ReplayVideoGridItem
                label="Start time"
                value={formatDateTimeWithTimeZone(
                  reportDetails?.info?.['Start time'],
                )}
              />
              <ReplayVideoGridItem
                label="End time"
                value={formatDateTimeWithTimeZone(
                  reportDetails?.info?.['End time'],
                )}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const ReplayVideoItem = ({ reportTemplate, report }: ReplayVideoItemProps) => {
  const { formatDateTimeWithTimeZone } = useDateTimeFormat();
  const { timeZone } = useTimeZoneContext();
  const [progress, setProgress] = React.useState<number>(
    report.percentComplete,
  );
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  const [showAbortDialog, setShowAbortDialog] = useState(false);

  const { data: reportDetails } = useGetReport(
    report.uuid,
    timeZone,
  );

  const onDelete = () => {
    setShowDeleteDialog(true);
  };

  const onClone = () => {
    setShowCloneDialog(true);
  };

  const onAbort = () => {
    setShowAbortDialog(true);
  };

  useEffect(() => {
    const timer = setTimeout(() => setProgress(report.percentComplete), 500);
    return () => clearTimeout(timer);
  }, [report.percentComplete]);

  if (report.status === 'NONE') {
    return null;
  }

  return (
    <AccordionItem key={report.uuid} value={report.uuid} className={cn('px-4')}>
      <div className="w-full flex items-center justify-between">
        <div className="flex-grow">
          <AccordionTrigger className="w-full" hideChevron={true}>
            <div className="flex flex-col w-full pr-4">
              <div className="flex items-center space-x-4">
                {report.status === 'FAILED' && report.failureReason ? (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="h-2.5 w-2.5 rounded-full bg-destructive" />
                    </TooltipTrigger>
                    <TooltipContent className="max-w-xs">
                      {report.failureReason}
                    </TooltipContent>
                  </Tooltip>
                ) : (
                  <div
                    className={cn(
                      'h-2.5 w-2.5 rounded-full',
                      report.status === 'FINISHED' && 'bg-success',
                      report.status === 'FAILED' && 'bg-destructive',
                      report.status === 'EXECUTING' && 'bg-blue-600',
                      report.status === 'PENDING' && 'bg-muted-foreground',
                      report.status === 'ABORTED' && 'bg-orange-400',
                    )}
                  />
                )}
                <div className="flex flex-col items-start">
                  <Label
                    className={cn('text-sm font-medium leading-none text-left')}
                  >
                    {reportTemplate.tplName} - {report.name}
                  </Label>
                  <Label className={cn('text-sm text-muted-foreground')}>
                    {formatDateTimeWithTimeZone(report.creationdate)}
                  </Label>
                </div>
              </div>

              {report.status !== 'FINISHED' &&
                report.status !== 'FAILED' &&
                report.status !== 'FINISHED_SCHEDULED' &&
                report.status !== 'ABORTED' && (
                  <div className="flex space-x-2 items-center pl-6 pt-1">
                    <p className="w-12">{progress} %</p>
                    <Progress value={progress} className="" />
                  </div>
                )}
            </div>
          </AccordionTrigger>
        </div>
        <div className="flex space-x-2">
          <ReplayDropDownMenu
            id={`replay-video-${report.name || 'unknown'}-${reportTemplate.tplName || 'unknown'}-${report.uuid}-dropdown-button`}
            report={report}
            reportTemplate={reportTemplate}
            onDelete={onDelete}
            onClone={onClone}
            onAbort={onAbort}
          ></ReplayDropDownMenu>
        </div>
        {showDeleteDialog && (
          <ReportDelete
            showDialog={showDeleteDialog}
            setShowDialog={setShowDeleteDialog}
            job={report}
            onBackToReports={noop}
          ></ReportDelete>
        )}
        {showAbortDialog && (
          <AbortReport
            showDialog={showAbortDialog}
            setShowDialog={setShowAbortDialog}
            job={report}
          />
        )}
        {showCloneDialog && (
          <CloneReport
            setShowPopover={noop}
            report={reportDetails}
            reportTemplate={reportTemplate}
            open={showCloneDialog}
            onOpenChange={setShowCloneDialog}
          />
        )}
      </div>
      <AccordionContent>
        <ReplayVideoDetails report={report}></ReplayVideoDetails>
      </AccordionContent>
    </AccordionItem>
  );
};

const MemoizedReplayVideoItem = React.memo(ReplayVideoItem, (prevProps, nextProps) => {
  // Only re-render if the report's key properties changed
  return (
    prevProps.report.uuid === nextProps.report.uuid &&
    prevProps.report.status === nextProps.report.status &&
    prevProps.report.percentComplete === nextProps.report.percentComplete &&
    prevProps.reportTemplate.tplUuid === nextProps.reportTemplate.tplUuid
  );
});

const ReplayVideoList = () => {
  const { data: reportTemplates, isLoading } = useGetReportTemplates(true);
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const [replayVideoTemplates, setReplayVideoTemplates] = useState<
    ReportTemplate[]
  >([]);

  useEffect(() => {
    if (reportTemplates) {
      const newReplayVideoTemplates = reportTemplates.filter((reportTemplate) => {
        return reportTemplate.type === ReportBackendTypes.REPLAY_VIDEO;
      });

      setReplayVideoTemplates(newReplayVideoTemplates);
    }
  }, [reportTemplates]);

  const {
    filteredAndSortedVideos,
    filters,
    setFilters,
    sortConfig,
    setSortConfig,
  } = useReplayVideoFilters(replayVideoTemplates, search);

  return (
    <Tooltip>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <TooltipTrigger asChild>
            <Button id="replay-videos-button" size="icon" variant="outline">
              <PiListBullets className="h-8 w-8"></PiListBullets>
            </Button>
          </TooltipTrigger>
        </PopoverTrigger>
        <PopoverContent className="w-[480px]" side="right" align="end">
          <CardHeader className="p-2 space-y-4">
            <div>
              <div className="flex justify-between">
                <CardTitle className="text-lg font-medium pb-2">
                  Replays
                </CardTitle>
                <Button
                  variant="ghost"
                  size="icon"
                  className="-mt-5 -mr-3"
                  onClick={() => setOpen(false)}
                >
                  <XIcon className="size-4" />
                </Button>
              </div>
              <Separator></Separator>
            </div>
            <div className="flex gap-2">
              <Input
                id="replay-search"
                placeholder="Search replays..."
                className="h-10 flex-1"
                value={search}
                onChange={(event) => setSearch(event.currentTarget.value)}
              />
              <ReplayVideoSort
                sortConfig={sortConfig}
                setSortConfig={setSortConfig}
              />
              <ReplayVideoFilter filters={filters} setFilters={setFilters} />
            </div>
          </CardHeader>
          <Accordion
            className="px-2 py-1 flex-1 flex flex-col overflow-hidden"
            type="single"
            collapsible
          >
            {isLoading && <LoadingSpinner></LoadingSpinner>}
            {!isLoading && (
              <InfiniteScroll
                items={filteredAndSortedVideos}
                limit={30}
                observeResize={true}
                className="flex-grow p-0 overflow-y-auto rounded-lg h-96 border"
                renderItem={(video: FlattenedReplayVideo) => (
                  <MemoizedReplayVideoItem
                    key={video.report.uuid}
                    reportTemplate={video.reportTemplate}
                    report={video.report}
                  />
                )}
              />
            )}
          </Accordion>
        </PopoverContent>
      </Popover>
      <TooltipContent>List replay videos</TooltipContent>
    </Tooltip>
  );
};

export const ReplayVideo = () => {
  const { data: replayRange } = useReplayRange();

  return (
    <div className="space-x-4">
      <Tooltip>
        <CreateReportProvider
          startDatetimeThreshold={replayRange?.startDatetime}
          endDatetimeThreshold={replayRange?.endDatetime}
        >
          <CreateReportTemplate>
            <TooltipTrigger asChild>
              <ReplayVideoButton></ReplayVideoButton>
            </TooltipTrigger>
          </CreateReportTemplate>
        </CreateReportProvider>
        <TooltipContent>Create a replay video</TooltipContent>
      </Tooltip>
      <ReplayVideoList></ReplayVideoList>
    </div>
  );
};

const ReplayVideoButton = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ onClick, size = 'icon', variant = 'outline', ...props }, ref) => {
    const { setChosenReportType, setDateRange: setReportDateRange } =
      useCreateReportContext();
    const { dateRange: replayDateRange } = useReplayContext();

    const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
      if (onClick) {
        onClick(e); // Trigger the custom onClick handler if provided
      }
      setChosenReportType(reportTypesObject[ReportBackendTypes.REPLAY_VIDEO]);
      setReportDateRange(replayDateRange);
    };

    return (
      <Button
        id="replay-create-video-button"
        ref={ref}
        size="icon"
        variant="outline"
        onClick={handleClick}
        {...props} // Spread additional props like className, type, etc.
      >
        <PiCameraPlus className="h-8 w-8" />
      </Button>
    );
  },
);
